import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2, Pencil } from 'lucide-react';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { apiClient } from '@/lib/apiClient';

interface PlaybookStep {
  title: string;
  description?: string;
  suggestedAction?: string;
}

interface PlaybookSummary {
  id: string;
  name: string;
  triggerKind: string;
  triggerValue: string | null;
  steps: PlaybookStep[];
  active: boolean;
  createdAt: string;
}

const TRIGGER_KINDS = ['STAGE_ENTRY', 'OPP_CREATED'] as const;

const emptyStep = (): PlaybookStep => ({ title: '', description: '', suggestedAction: '' });

export function PlaybooksAdminPage() {
  const { t } = useTranslation();
  const [playbooks, setPlaybooks] = useState<PlaybookSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<string | null>(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [triggerKind, setTriggerKind] = useState<string>('STAGE_ENTRY');
  const [triggerValue, setTriggerValue] = useState('');
  const [steps, setSteps] = useState<PlaybookStep[]>([emptyStep()]);
  const [saving, setSaving] = useState(false);

  function showToast(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  }

  const load = useCallback(async () => {
    try {
      const { data } = await apiClient.get<PlaybookSummary[]>('/admin/playbooks');
      setPlaybooks(data);
    } catch {
      setPlaybooks([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  function openCreate() {
    setEditId(null);
    setName('');
    setTriggerKind('STAGE_ENTRY');
    setTriggerValue('');
    setSteps([emptyStep()]);
    setDialogOpen(true);
  }

  function openEdit(pb: PlaybookSummary) {
    setEditId(pb.id);
    setName(pb.name);
    setTriggerKind(pb.triggerKind);
    setTriggerValue(pb.triggerValue ?? '');
    setSteps(pb.steps.length > 0 ? pb.steps : [emptyStep()]);
    setDialogOpen(true);
  }

  async function save() {
    if (!name.trim()) return;
    setSaving(true);
    const payload = {
      name: name.trim(),
      triggerKind,
      triggerValue: triggerValue.trim() || null,
      steps: steps.filter((s) => s.title.trim()).map((s) => ({
        title: s.title.trim(),
        description: s.description?.trim() || null,
        suggestedAction: s.suggestedAction?.trim() || null,
      })),
    };
    try {
      if (editId) {
        await apiClient.put(`/admin/playbooks/${editId}`, payload);
      } else {
        await apiClient.post('/admin/playbooks', payload);
      }
      showToast(t('playbook.saved'));
      setDialogOpen(false);
      load();
    } catch {
      showToast(t('playbook.saved') + ' — error');
    } finally {
      setSaving(false);
    }
  }

  async function del(id: string) {
    if (!window.confirm(t('playbook.delete_confirm'))) return;
    try {
      await apiClient.delete(`/admin/playbooks/${id}`);
      showToast(t('playbook.deleted'));
      load();
    } catch {
      showToast(t('playbook.deleted') + ' — error');
    }
  }

  function updateStep(idx: number, field: keyof PlaybookStep, value: string) {
    setSteps((prev) => prev.map((s, i) => i === idx ? { ...s, [field]: value } : s));
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <Breadcrumbs crumbs={[{ label: t('playbook.admin_title') }]} />
      <div className="flex items-center justify-between mt-2">
        <div>
          <h1 className="text-2xl font-semibold">{t('playbook.admin_title')}</h1>
          <p className="text-muted-foreground text-sm mt-1">{t('playbook.admin_subtitle')}</p>
        </div>
        <Button onClick={openCreate} className="gap-1">
          <Plus className="h-4 w-4" /> {t('playbook.new_button')}
        </Button>
      </div>

      {toast && (
        <div className="fixed bottom-4 right-4 bg-foreground text-background px-4 py-2 rounded shadow z-50 text-sm">
          {toast}
        </div>
      )}

      {loading ? (
        <p className="text-muted-foreground mt-6 text-sm">{t('common.loading')}</p>
      ) : playbooks.length === 0 ? (
        <p className="text-muted-foreground mt-6 text-sm">{t('playbook.empty')}</p>
      ) : (
        <div className="mt-6 border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-muted-foreground">
              <tr>
                <th className="text-left px-4 py-2">{t('playbook.col_name')}</th>
                <th className="text-left px-4 py-2">{t('playbook.col_trigger')}</th>
                <th className="text-left px-4 py-2">{t('playbook.col_steps')}</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {playbooks.map((pb) => (
                <tr key={pb.id} className="hover:bg-muted/20">
                  <td className="px-4 py-3 font-medium">{pb.name}</td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {t(`playbook.trigger_${pb.triggerKind}`)}
                    {pb.triggerValue && <span className="ml-1 text-xs text-muted-foreground/60">({pb.triggerValue.slice(0, 8)}…)</span>}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{pb.steps.length}</td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      <Button size="sm" variant="ghost" onClick={() => openEdit(pb)}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => del(pb.id)}>
                        <Trash2 className="h-3.5 w-3.5 text-destructive" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create/Edit dialog */}
      {dialogOpen && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4 overflow-y-auto">
          <div className="my-8 w-full max-w-xl bg-card border rounded-xl shadow-xl p-6">
            <h2 className="font-semibold text-lg mb-4">
              {editId ? t('playbook.dialog_edit_title') : t('playbook.dialog_create_title')}
            </h2>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium">{t('playbook.field_name')}</label>
                <Input className="mt-1" value={name} onChange={(e) => setName(e.target.value)} />
              </div>
              <div>
                <label className="text-sm font-medium">{t('playbook.field_trigger_kind')}</label>
                <select
                  className="mt-1 w-full border rounded-md bg-background px-3 py-2 text-sm"
                  value={triggerKind}
                  onChange={(e) => setTriggerKind(e.target.value)}
                >
                  {TRIGGER_KINDS.map((k) => (
                    <option key={k} value={k}>{t(`playbook.trigger_${k}`)}</option>
                  ))}
                </select>
              </div>
              {triggerKind === 'STAGE_ENTRY' && (
                <div>
                  <label className="text-sm font-medium">{t('playbook.field_trigger_value')}</label>
                  <Input
                    className="mt-1"
                    placeholder="UUID of the stage"
                    value={triggerValue}
                    onChange={(e) => setTriggerValue(e.target.value)}
                  />
                </div>
              )}

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium">{t('playbook.steps_title')}</label>
                  <Button size="sm" variant="outline" onClick={() => setSteps((prev) => [...prev, emptyStep()])}>
                    {t('playbook.add_step')}
                  </Button>
                </div>
                <div className="space-y-3">
                  {steps.map((step, idx) => (
                    <div key={idx} className="border rounded-md p-3 bg-muted/30 space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-medium text-muted-foreground">Step {idx + 1}</span>
                        {steps.length > 1 && (
                          <button
                            onClick={() => setSteps((prev) => prev.filter((_, i) => i !== idx))}
                            className="text-xs text-destructive hover:underline"
                          >
                            {t('playbook.remove_step')}
                          </button>
                        )}
                      </div>
                      <Input
                        placeholder={t('playbook.step_title')}
                        value={step.title}
                        onChange={(e) => updateStep(idx, 'title', e.target.value)}
                      />
                      <Input
                        placeholder={t('playbook.step_description')}
                        value={step.description ?? ''}
                        onChange={(e) => updateStep(idx, 'description', e.target.value)}
                      />
                      <Input
                        placeholder={t('playbook.step_action')}
                        value={step.suggestedAction ?? ''}
                        onChange={(e) => updateStep(idx, 'suggestedAction', e.target.value)}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>
                {t('common.cancel')}
              </Button>
              <Button onClick={save} disabled={saving || !name.trim()}>
                {saving ? t('playbook.saving') : t('playbook.save')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
